// src/components/resources/quizzes/components/QuizRecommendations.tsx

'use client'

import { type ReactNode } from 'react'
import { cn } from '~/lib/utils'
import type { QuizResult, QuizSection, QuizQuestion, QuizRecommendation, RecommendationItem } from '~/lib/resources/types/tools/quiz'
import { 
  getUrlFromSupplementalResource, 
  isExternalSupplementalResource, 
} from '~/lib/resources/utils/resourceUtils'
import RiExternalLinkLine from '~/icons/remix/generated/RiExternalLinkLine'
import RiBookOpenLine from '~/icons/remix/generated/RiBookOpenLine'
import RiVideoLine from '~/icons/remix/generated/RiVideoLine'
import RiToolsFill from '~/icons/remix/generated/RiToolsFill'
import RiFileLine from '~/icons/remix/generated/RiFileLine'
import RiBookmarkLine from '~/icons/remix/generated/RiBookmarkLine'
import RiQuestionLine from '~/icons/remix/generated/RiQuestionLine'
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import RiBriefcaseLine from '~/icons/remix/generated/RiBriefcaseLine'
import RiBookLine from '~/icons/remix/generated/RiBookLine'
import RiFileList3Line from '~/icons/remix/generated/RiFileList3Line'
import RiFileTextLine from '~/icons/remix/generated/RiFileTextLine'
import RiCodeSSlashLine from '~/icons/remix/generated/RiCodeSSlashLine'
import RiLinksLine from '~/icons/remix/generated/RiLinksLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import type { SupplementalResourceReference } from '~/lib/resources/types/shared/base'

interface QuizRecommendationsProps {
  result: QuizResult
  className?: string
}

export function QuizRecommendations({
  result,
  className,
}: QuizRecommendationsProps): ReactNode {
  // Helper to format section/category names
  const formatName = (name: string): string => {
    return name
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Collect all recommendations from different sources
  const allRecommendations = [
    // Overall recommendations
    ...result.feedback.recommendations.map(rec => ({
      source: 'Overall',
      priority: 'high' as const,
      item: rec
    })),

    // Section recommendations
    ...Object.entries(result.sectionResults)
      .filter(([, score]) => score.feedback?.recommendations)
      .flatMap(([sectionId, score]) =>
        (score.feedback?.recommendations ?? []).map(rec => ({
          source: `Section: ${formatName(sectionId)}`,
          priority: score.percentage < 70 ? 'high' as const : 'medium' as const,
          item: rec
        }))
      ),

    // Category recommendations
    ...Object.entries(result.categoryResults ?? {})
      .filter(([, score]) => score.feedback?.recommendations)
      .flatMap(([category, score]) =>
        (score.feedback?.recommendations ?? []).map(rec => ({
          source: `Category: ${formatName(category)}`,
          priority: score.percentage < 70 ? 'high' as const : 'medium' as const,
          item: rec
        }))
      )
  ]

  if (!allRecommendations.length) return null

  // Sort recommendations by priority and source
  const sortedRecommendations = allRecommendations.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 }
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
    
    // If same priority, sort by source (Overall first, then Sections, then Categories)
    if (priorityDiff === 0) {
      if (a.source === 'Overall') return -1
      if (b.source === 'Overall') return 1
      if (a.source.startsWith('Section:') && !b.source.startsWith('Section:')) return -1
      if (!a.source.startsWith('Section:') && b.source.startsWith('Section:')) return 1
      return a.source.localeCompare(b.source)
    }
    return priorityDiff
  })

  // Group recommendations by source
  const groupedRecommendations = sortedRecommendations.reduce((groups, rec) => {
    const group = groups.find(g => g.source === rec.source)
    if (group) {
      group.recommendations.push(rec.item)
    } else {
      groups.push({
        source: rec.source,
        priority: rec.priority,
        recommendations: [rec.item]
      })
    }
    return groups
  }, [] as Array<{
    source: string
    priority: 'high' | 'medium' | 'low'
    recommendations: RecommendationItem[]
  }>)

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50/50 p-6 shadow-lg ring-1 ring-indigo-900/10 dark:from-indigo-900/20 dark:to-blue-900/10 dark:ring-indigo-400/10',
        className
      )}
    >
      <h3 className="flex items-center gap-3 text-lg font-semibold text-indigo-900 dark:text-indigo-300">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/50">
          <RiLightbulbLine className="h-5 w-5 text-indigo-700 dark:text-indigo-300" />
        </span>
        Recommended Next Steps
      </h3>

      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {groupedRecommendations.map((group, groupIndex) => (
          <div 
            key={groupIndex} 
            className="overflow-hidden rounded-lg bg-white/50 shadow-sm ring-1 ring-indigo-900/10 dark:bg-indigo-900/30 dark:ring-indigo-400/10"
          >
            {/* Group Header */}
            <div className="border-b border-indigo-900/10 bg-white/80 px-4 py-3 dark:border-indigo-400/10 dark:bg-indigo-900/40">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium',
                    group.priority === 'high'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      : group.priority === 'medium'
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  )}
                >
                  {groupIndex + 1}
                </span>
                <h4 className="text-base font-medium text-indigo-900 dark:text-indigo-300">
                  {group.source}
                </h4>
              </div>
            </div>

            {/* Group Content */}
            {group.recommendations.length > 0 && (
              <div className="divide-y divide-indigo-900/5 dark:divide-indigo-400/5">
                {group.recommendations.map((rec, recIndex) => 
                  renderRecommendation(rec, recIndex)
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

interface PerformanceMetrics {
  overallScore: number
  sectionScores: Record<string, number>
  categoryScores: Record<string, number>
  platformScores: Record<string, number>
  weakestAreas: string[]
  strongestAreas: string[]
}

export function analyzePerformance(
  result: QuizResult,
  sections: QuizSection[]
): PerformanceMetrics {
  const metrics: PerformanceMetrics = {
    overallScore: result.score,
    sectionScores: {},
    categoryScores: {},
    platformScores: {},
    weakestAreas: [],
    strongestAreas: [],
  }

  // Calculate section scores from sectionResults
  Object.entries(result.sectionResults).forEach(([sectionId, score]) => {
    metrics.sectionScores[sectionId] = score.percentage

    const section = sections.find((s) => s.id === sectionId)
    if (section) {
      if (score.percentage < 60) {
        metrics.weakestAreas.push(section.title)
      } else if (score.percentage >= 80) {
        metrics.strongestAreas.push(section.title)
      }
    }
  })

  // Calculate platform scores if available
  if (result.platformResults) {
    Object.entries(result.platformResults).forEach(([platform, score]) => {
      metrics.platformScores[platform] = score.percentage
    })
  }

  // Calculate category scores if available
  if (result.categoryResults) {
    Object.entries(result.categoryResults).forEach(([category, score]) => {
      metrics.categoryScores[category] = score.percentage
    })
  }

  return metrics
}

export function generateRecommendations(
  metrics: PerformanceMetrics,
  sections: QuizSection[],
  questions: QuizQuestion[]
): QuizRecommendation[] {
  const recommendations: QuizRecommendation[] = []

  // Helper to format names consistently
  const formatName = (name: string): string => {
    return name
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Add recommendations for weak areas
  metrics.weakestAreas.forEach((area) => {
    const section = sections.find((s) => s.title === area)
    if (section) {
      recommendations.push({
        text: `Review ${formatName(area)} concepts to improve your understanding`,
        resources: section.questions
          .filter((q) => q.learningContent?.resources)
          .flatMap((q) => q.learningContent?.resources ?? [])
          .map((r) => ({
            title: r.title ?? 'Resource',
            learningResourceType: r.learningResourceType,
            urlSlug: r.urlSlug ?? r.resourceSlug ?? r.slug ?? '',
            baseUrl: r.baseUrl,
            description: r.description,
            isInternal: r.isInternal ?? false
          })),
        priority: 'high',
        category: 'improvement',
      })
    }
  })

  // Add platform-specific recommendations
  Object.entries(metrics.platformScores).forEach(([platform, score]) => {
    if (score < 70) {
      recommendations.push({
        text: `Strengthen your knowledge of ${formatName(platform)} best practices`,
        resources: questions
          .filter(
            (q) => q.platform === platform && q.learningContent?.resources
          )
          .flatMap((q) => q.learningContent?.resources ?? [])
          .map((r) => ({
            title: r.title ?? 'Resource',
            learningResourceType: r.learningResourceType,
            urlSlug: r.urlSlug ?? r.resourceSlug ?? r.slug ?? '',
            baseUrl: r.baseUrl,
            description: r.description,
            isInternal: r.isInternal ?? false
          })),
        priority: 'medium',
        category: 'platform',
      })
    }
  })

  // Add category-specific recommendations
  Object.entries(metrics.categoryScores).forEach(([category, score]) => {
    if (score < 70) {
      recommendations.push({
        text: `Focus on improving your ${formatName(category)} skills`,
        resources: questions
          .filter(
            (q) => q.category === category && q.learningContent?.resources
          )
          .flatMap((q) => q.learningContent?.resources ?? [])
          .map((r) => ({
            title: r.title ?? 'Resource',
            learningResourceType: r.learningResourceType,
            urlSlug: r.urlSlug ?? r.resourceSlug ?? r.slug ?? '',
            baseUrl: r.baseUrl,
            description: r.description,
            isInternal: r.isInternal ?? false
          })),
        priority: score < 50 ? 'high' : 'medium',
        category: 'category',
      })
    }
  })

  return recommendations
}

/**
 * Gets the appropriate icon for a SupplementalResourceReference
 */
export function getResourceIconForSupplemental(resource: SupplementalResourceReference, iconProps: { className?: string }): React.ReactNode {
  if (!resource.isInternal) {
    return <RiExternalLinkLine className={iconProps.className} />
  }
  
  // Select icon based on resource type
  switch(resource.learningResourceType.toLowerCase()) {
    case 'article':
      return <RiBookOpenLine className={iconProps.className} />
    case 'video':
      return <RiVideoLine className={iconProps.className} />
    case 'tool':
      return <RiToolsFill className={iconProps.className} />
    case 'documentation':
      return <RiFileLine className={iconProps.className} />
    case 'guide':
      return <RiBookmarkLine className={iconProps.className} />
    case 'glossary':
      return <RiBookmarkLine className={iconProps.className} />
    case 'glossary-term':
      return <RiBookmarkLine className={iconProps.className} />
    case 'quiz':
      return <RiQuestionLine className={iconProps.className} />
    case 'calculator':
      return <RiCalculatorLine className={iconProps.className} />
    case 'case-study':
      return <RiBriefcaseLine className={iconProps.className} />
    case 'course':
      return <RiBookLine className={iconProps.className} />
    case 'template':
      return <RiFileList3Line className={iconProps.className} />
    case 'tutorial':
      return <RiBookLine className={iconProps.className} />
    case 'ebook':
      return <RiFileTextLine className={iconProps.className} />
    case 'generator':
      return <RiCodeSSlashLine className={iconProps.className} />
    case 'analyzer':
      return <RiToolsFill className={iconProps.className} />
    default:
      return <RiLinksLine className={iconProps.className} />
  }
}
// Function to render a recommendation item - can be string or SupplementalResourceReference
function renderRecommendation(rec: RecommendationItem, index: number): JSX.Element {
  // If it's just a string, render it as a simple bullet point
  if (typeof rec === 'string') {
    return (
      <div key={index} className="p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {rec}
        </p>
      </div>
    )
  }

  // Otherwise it's a SupplementalResourceReference - render it as a link card
  return (
    <div key={index} className="p-4">
      <a
        href={getUrlFromSupplementalResource(rec)}
        className="group flex items-start gap-3"
        target={isExternalSupplementalResource(rec) ? "_blank" : undefined}
        rel={isExternalSupplementalResource(rec) ? "noopener noreferrer" : undefined}
      >
        <div className="mt-1 flex-none rounded bg-indigo-100 p-1.5 text-indigo-600 transition-colors 
        group-hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-400 dark:group-hover:bg-indigo-900/60">
          {getResourceIconForSupplemental(rec, { className: "h-4 w-4 transition-transform group-hover:translate-x-0.5" })}
        </div>
        <div>
          <div className="text-sm font-medium text-indigo-600 group-hover:text-indigo-700 dark:text-indigo-400 
          dark:group-hover:text-indigo-300">
            {rec.title}
          </div>
          {rec.description && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {rec.description}
            </p>
          )}
        </div>
      </a>
    </div>
  )
}